/**
 * AnalyticsPage — portfolio analytics (trend charts + cross-client overview)
 *
 * Route: /analytics
 * Moved from DashboardPage "Analytics" tab — now a top-level nav destination.
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { useAuth, isAdmin } from '../context/AuthContext'
import { apiFetch } from '../lib/apiClient'
import styles from './DashboardPage.module.css'
import rulesStyles from './ScoringRulesPanel.module.css'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RunRecord {
  run_id:           number
  run_label:        string
  run_date:         string
  engagement_name:  string
  client_name:      string
  instrument_code:  string
  respondent_count: number
  avg_readiness:    number | null
  avg_resistance:   number | null
  avg_index:        number | null
}

interface TrendPoint {
  run_id:           number
  run_label:        string
  run_date:         string
  respondent_count: number
  avg_readiness:    number | null
  avg_resistance:   number | null
  avg_index:        number | null
}

interface EngagementTrend {
  engagement_id:   number
  engagement_name: string
  client_name:     string
  instrument_code: string
  points:          TrendPoint[]
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

async function fetchRuns(idToken: string | null): Promise<RunRecord[]> {
  return apiFetch<RunRecord[]>('/dashboard/runs', idToken)
}

async function fetchTrends(idToken: string | null): Promise<EngagementTrend[]> {
  return apiFetch<EngagementTrend[]>('/dashboard/trends', idToken)
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const LABEL_ORDER = ['Baseline', 'Pre-Go-Live', '30-Day', '90-Day', 'Custom']

function InstrumentTag({ code }: { code: string }) {
  const codec = code === 'CODEC-v3'
  return (
    <span className={`${styles.instrTag} ${codec ? styles.instrCodec : styles.instrHfa}`}>
      {codec ? 'CODE-C' : 'HFA'}
    </span>
  )
}

// ---------------------------------------------------------------------------
// TrendChart — SVG sparkline for score progression
// ---------------------------------------------------------------------------

function TrendChart({ trend }: {
  trend: EngagementTrend
}) {
  const isCodec = trend.instrument_code === 'CODEC-v3'
  const W = 340, H = 120, PAD = { top: 12, right: 16, bottom: 28, left: 40 }
  const innerW = W - PAD.left - PAD.right
  const innerH = H - PAD.top - PAD.bottom

  const pts = [...trend.points].sort(
    (a, b) => LABEL_ORDER.indexOf(a.run_label) - LABEL_ORDER.indexOf(b.run_label),
  )

  const primary   = pts.map(p => isCodec ? p.avg_readiness : p.avg_index)
  const secondary = isCodec ? pts.map(p => p.avg_resistance) : []

  const allVals = [...primary, ...secondary].filter((v): v is number => v !== null)
  const minV = Math.max(0,   Math.min(...allVals) - 10)
  const maxV = Math.min(100, Math.max(...allVals) + 10)
  const range = maxV - minV || 1

  const xOf = (i: number) => PAD.left + (i / (pts.length - 1)) * innerW
  const yOf = (v: number | null) =>
    v === null ? null : PAD.top + innerH - ((v - minV) / range) * innerH

  function polyline(vals: (number | null)[]) {
    return vals
      .map((v, i) => [xOf(i), yOf(v)] as [number, number | null])
      .filter(([, y]) => y !== null)
      .map(([x, y]) => `${x},${y}`)
      .join(' ')
  }

  // Semantic: readiness/HFA = "positive" metric → strong color; HFA uses same direction
  const accentColor = 'var(--strong)'

  return (
    <div className={styles.trendCard}>
      <div className={styles.trendCardHeader}>
        <span className={styles.trendEngName}>{trend.engagement_name}</span>
        <span className={styles.trendClientName}>{trend.client_name}</span>
        <InstrumentTag code={trend.instrument_code} />
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className={styles.trendSvg}>
        {[0, 25, 50, 75, 100].map(v => {
          const y = yOf(v)
          if (y === null || y < PAD.top || y > H - PAD.bottom) return null
          return (
            <g key={v}>
              <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y}
                stroke="#e5e7eb" strokeWidth="1" />
              <text x={PAD.left - 6} y={y + 4} textAnchor="end"
                fontSize="9" fill="#9ca3af">{v}</text>
            </g>
          )
        })}
        {isCodec && secondary.length > 0 && (
          <polyline points={polyline(secondary)} fill="none"
            stroke="var(--needs)" strokeWidth="1.5"
            strokeDasharray="4 3" opacity="0.7" />
        )}
        <polyline points={polyline(primary)} fill="none"
          stroke={accentColor} strokeWidth="2"
          strokeLinejoin="round" strokeLinecap="round" />
        {pts.map((pt, i) => {
          const v = isCodec ? pt.avg_readiness : pt.avg_index
          const y = yOf(v)
          if (y === null) return null
          return (
            <g key={pt.run_id}>
              <circle cx={xOf(i)} cy={y} r="4" fill={accentColor} />
              <text x={xOf(i)} y={H - PAD.bottom + 14} textAnchor="middle"
                fontSize="9" fill="#6b7280">
                {pt.run_label.replace('-Day', 'd')}
              </text>
              <text x={xOf(i)} y={y - 8} textAnchor="middle"
                fontSize="9" fontWeight="700" fill={accentColor}>
                {v !== null ? `${Math.round(v)}%` : ''}
              </text>
            </g>
          )
        })}
      </svg>
      {isCodec && (
        <div className={styles.trendLegend}>
          <span className={styles.trendLegendDot} style={{ background: 'var(--strong)' }} />
          <span>Readiness</span>
          <span className={styles.trendLegendDash} />
          <span style={{ color: 'var(--needs)' }}>Resistance</span>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// AnalyticsPanel — cross-client overview + trend charts
// ---------------------------------------------------------------------------

function AnalyticsPanel({ runs, trends }: { runs: RunRecord[]; trends: EngagementTrend[] }) {
  const clientStats = useMemo(() => {
    const map = new Map<string, {
      client: string
      codecReady: number[]; codecResist: number[]; hfaIndex: number[]; runCount: number
    }>()
    for (const r of runs) {
      if (!map.has(r.client_name)) {
        map.set(r.client_name, {
          client: r.client_name,
          codecReady: [], codecResist: [], hfaIndex: [], runCount: 0,
        })
      }
      const s = map.get(r.client_name)!
      s.runCount++
      if (r.avg_readiness  !== null) s.codecReady.push(r.avg_readiness)
      if (r.avg_resistance !== null) s.codecResist.push(r.avg_resistance)
      if (r.avg_index      !== null) s.hfaIndex.push(r.avg_index)
    }
    return Array.from(map.values()).map(s => ({
      ...s,
      avgReady:  s.codecReady.length  ? s.codecReady.reduce((a, b) => a + b, 0)  / s.codecReady.length  : null,
      avgResist: s.codecResist.length ? s.codecResist.reduce((a, b) => a + b, 0) / s.codecResist.length : null,
      avgIndex:  s.hfaIndex.length    ? s.hfaIndex.reduce((a, b) => a + b, 0)    / s.hfaIndex.length    : null,
    })).sort((a, b) => a.client.localeCompare(b.client))
  }, [runs])

  // invert=true for resistance: low resistance is good → green; high → red
  function ScoreBar2({ score, invert = false }: { score: number | null; invert?: boolean }) {
    if (score === null) return <span className={styles.noScore}>—</span>
    const effective = invert ? 100 - score : score
    const bandColor = effective >= 75 ? 'var(--strong)' : effective >= 50 ? 'var(--developing)' : 'var(--needs)'
    return (
      <div className={styles.analyticsBarWrap}>
        <div className={styles.analyticsBar}
          style={{ width: `${Math.min(score, 100)}%`, background: bandColor }} />
        <span className={styles.analyticsBarLabel}>{Math.round(score)}%</span>
      </div>
    )
  }

  return (
    <div className={styles.analyticsPanel}>
      <section className={styles.analyticsSection}>
        <div className={styles.analyticsSectionLabel}>Portfolio Overview — all clients</div>
        {clientStats.length === 0 ? (
          <p className={styles.noDataNote}>No client data yet.</p>
        ) : (
          <div className={styles.analyticsTable}>
            <div className={styles.analyticsTableHeader}>
              <span>Client</span>
              <span>Runs</span>
              <span>CODE-C Readiness</span>
              <span>CODE-C Resistance</span>
              <span>HFA Index</span>
            </div>
            {clientStats.map(c => (
              <div key={c.client} className={styles.analyticsTableRow}>
                <span className={styles.analyticsClientName}>{c.client}</span>
                <span className={styles.analyticsRunCount}>{c.runCount}</span>
                <ScoreBar2 score={c.avgReady} />
                <ScoreBar2 score={c.avgResist} invert />
                <ScoreBar2 score={c.avgIndex} />
              </div>
            ))}
          </div>
        )}
      </section>

      <section className={styles.analyticsSection}>
        <div className={styles.analyticsSectionLabel}>
          Score Trends — engagements with 2+ runs
        </div>
        {trends.length === 0 ? (
          <p className={styles.noDataNote}>
            Trend charts appear once an engagement has 2 or more runs
            (e.g., Baseline + 30-Day). Create additional runs in the Setup portal.
          </p>
        ) : (
          <div className={styles.trendGrid}>
            {trends.map(t => (
              <TrendChart
                key={`${t.engagement_id}-${t.instrument_code}`}
                trend={t}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ScoringConfig types
// ---------------------------------------------------------------------------

interface ScoringConfig {
  band_thresholds: { strong: number; developing: number }
  hfa_weights:     { outcomes: number; mechanics: number }
  updated_by:      string | null
  updated_at:      string | null
}

// ---------------------------------------------------------------------------
// ScoringRulesPanel — clutch_admin only
// ---------------------------------------------------------------------------

function ScoringRulesPanel({ idToken }: { idToken: string | null }) {
  const [config,  setConfig]  = useState<ScoringConfig | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  // Form state (strings so inputs stay controlled)
  const [strong,     setStrong]     = useState('')
  const [developing, setDeveloping] = useState('')
  const [outcomes,   setOutcomes]   = useState('')
  const [mechanics,  setMechanics]  = useState('')

  const loadConfig = useCallback(async () => {
    if (!idToken) return
    setLoading(true); setError(null)
    try {
      const data = await apiFetch<ScoringConfig>('/config/scoring', idToken)
      setConfig(data)
      setStrong(String(data.band_thresholds.strong))
      setDeveloping(String(data.band_thresholds.developing))
      setOutcomes(String(Math.round(data.hfa_weights.outcomes * 100)))
      setMechanics(String(Math.round(data.hfa_weights.mechanics * 100)))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load scoring config.')
    } finally {
      setLoading(false)
    }
  }, [idToken])

  useEffect(() => { loadConfig() }, [loadConfig])

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setError(null); setSuccess(null)

    const strongVal     = parseInt(strong, 10)
    const developingVal = parseInt(developing, 10)
    const outcomesVal   = parseFloat(outcomes) / 100
    const mechanicsVal  = parseFloat(mechanics) / 100

    if (isNaN(strongVal) || isNaN(developingVal))  { setError('Band thresholds must be integers.'); return }
    if (developingVal >= strongVal)                  { setError('"Developing" threshold must be below "Strong".'); return }
    if (isNaN(outcomesVal) || isNaN(mechanicsVal))  { setError('HFA weights must be numbers.'); return }
    if (Math.abs(outcomesVal + mechanicsVal - 1) > 0.01) { setError('HFA weights must sum to 100%.'); return }

    setSaving(true)
    try {
      await apiFetch('/config/scoring', idToken, {
        method: 'PATCH',
        body: JSON.stringify({
          strong_threshold:     strongVal,
          developing_threshold: developingVal,
          hfa_outcomes_weight:  outcomesVal,
          hfa_mechanics_weight: mechanicsVal,
        }),
      })
      setSuccess('Scoring rules saved. Changes apply to new assessments only.')
      await loadConfig()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <section className={rulesStyles.panel} aria-label="Scoring rules">
      <div className={rulesStyles.header}>
        <h2 className={rulesStyles.title}>Scoring Rules</h2>
        <span className={rulesStyles.adminOnly}>Principal Consultant only</span>
      </div>
      <p className={rulesStyles.note}>
        Changes apply to <strong>new</strong> assessments only. Historical scores are frozen.
      </p>

      {loading && <p className={rulesStyles.loading}>Loading…</p>}
      {error   && <div className={rulesStyles.errorBox} role="alert">{error}</div>}
      {success && <div className={rulesStyles.successBox} role="status">{success}</div>}

      {config && !loading && (
        <form onSubmit={handleSave} noValidate>
          <div className={rulesStyles.grid}>

            {/* Band thresholds */}
            <div className={rulesStyles.block}>
              <h3 className={rulesStyles.blockTitle}>Score bands (0–100 scale)</h3>
              <p className={rulesStyles.blockNote}>
                Applies to CODE-C Readiness, CODE-C Resistance, and HFA Index.
              </p>
              <div className={rulesStyles.thresholdRow}>
                <span className="band-strong">Strong</span>
                <span className={rulesStyles.op}>≥</span>
                <input
                  className={rulesStyles.numInput}
                  type="number" min="51" max="99" step="1"
                  value={strong}
                  onChange={e => setStrong(e.target.value)}
                  aria-label="Strong threshold"
                />
              </div>
              <div className={rulesStyles.thresholdRow}>
                <span className="band-developing">Developing</span>
                <span className={rulesStyles.op}>≥</span>
                <input
                  className={rulesStyles.numInput}
                  type="number" min="1" max="74" step="1"
                  value={developing}
                  onChange={e => setDeveloping(e.target.value)}
                  aria-label="Developing threshold"
                />
              </div>
              <div className={rulesStyles.thresholdRow}>
                <span className="band-needs">Needs Support</span>
                <span className={rulesStyles.op}>&lt;</span>
                <span className={rulesStyles.numDisplay}>{developing || '50'}</span>
              </div>
            </div>

            {/* HFA index weights */}
            <div className={rulesStyles.block}>
              <h3 className={rulesStyles.blockTitle}>HFA Index weights</h3>
              <p className={rulesStyles.blockNote}>
                Habit Adoption Index = (Outcomes × w₁) + (Mechanics × w₂). Must sum to 100%.
              </p>
              <div className={rulesStyles.weightRow}>
                <label className={rulesStyles.weightLabel}>Outcomes (EE + PR + BO)</label>
                <div className={rulesStyles.weightInputWrap}>
                  <input
                    className={rulesStyles.numInput}
                    type="number" min="0" max="100" step="1"
                    value={outcomes}
                    onChange={e => {
                      const v = parseInt(e.target.value, 10)
                      setOutcomes(e.target.value)
                      if (!isNaN(v)) setMechanics(String(100 - v))
                    }}
                    aria-label="Outcomes weight %"
                  />
                  <span className={rulesStyles.pct}>%</span>
                </div>
              </div>
              <div className={rulesStyles.weightRow}>
                <label className={rulesStyles.weightLabel}>Mechanics (CT + RF)</label>
                <div className={rulesStyles.weightInputWrap}>
                  <input
                    className={rulesStyles.numInput}
                    type="number" min="0" max="100" step="1"
                    value={mechanics}
                    onChange={e => {
                      const v = parseInt(e.target.value, 10)
                      setMechanics(e.target.value)
                      if (!isNaN(v)) setOutcomes(String(100 - v))
                    }}
                    aria-label="Mechanics weight %"
                  />
                  <span className={rulesStyles.pct}>%</span>
                </div>
              </div>
              <p className={rulesStyles.sumLine}>
                Sum: {(parseFloat(outcomes) || 0) + (parseFloat(mechanics) || 0)}%
                {Math.abs((parseFloat(outcomes) || 0) + (parseFloat(mechanics) || 0) - 100) < 1
                  ? ' ✓' : ' — must equal 100%'}
              </p>
            </div>
          </div>

          <div className={rulesStyles.footer}>
            {config.updated_by && config.updated_at && (
              <p className={rulesStyles.lastUpdated}>
                Last updated by {config.updated_by} on{' '}
                {new Date(config.updated_at).toLocaleDateString('en-US', {
                  month: 'short', day: 'numeric', year: 'numeric',
                })}
              </p>
            )}
            <button
              className={rulesStyles.saveBtn}
              type="submit"
              disabled={saving}
              aria-busy={saving}
            >
              {saving ? 'Saving…' : 'Save scoring rules'}
            </button>
          </div>
        </form>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AnalyticsPage() {
  const { idToken, platformUser } = useAuth()
  const [runs,    setRuns]    = useState<RunRecord[]>([])
  const [trends,  setTrends]  = useState<EngagementTrend[]>([])
  const [loading, setLoading] = useState(true)
  const [error,   setError]   = useState('')

  useEffect(() => {
    if (idToken === undefined) return   // still resolving auth
    Promise.all([fetchRuns(idToken), fetchTrends(idToken)])
      .then(([r, t]) => { setRuns(r); setTrends(t); setLoading(false) })
      .catch(e => { setError(e instanceof Error ? e.message : 'Error'); setLoading(false) })
  }, [idToken])

  if (loading) {
    return (
      <div className={styles.stateScreen}>
        <div className={styles.spinner} />
        <p>Loading analytics…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.stateScreen}>
        <p className={styles.errorMsg}>Failed to load analytics: {error}</p>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <div className={styles.eyebrow}>Platform Analytics</div>
          <h1 className={styles.pageTitle}>Analytics</h1>
          <p className={styles.pageSub}>
            {runs.length} runs across {new Set(runs.map(r => r.client_name)).size} client{new Set(runs.map(r => r.client_name)).size !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
      <AnalyticsPanel runs={runs} trends={trends} />
      {isAdmin(platformUser) && <ScoringRulesPanel idToken={idToken ?? null} />}
    </div>
  )
}
